/**
 * EvaluationPeriodSection - Accordion section for grouping modules by evaluation period
 */

import { useDroppable } from '@dnd-kit/core';
import { SortableContext, verticalListSortingStrategy } from '@dnd-kit/sortable';
import type { EvaluationPeriod, ModuleItemType, SubmoduleItem } from 'app/types/modules';
import clsx from 'clsx';
import { useTranslate } from 'core/hooks/useTranslate';
import Icon from '../../../../atoms/Icon/Icon';
import { stripHtml } from 'core/utils/utils';
import React, { useMemo } from 'react';
import AddItemButton from './AddItemButton';
import ModuleRow from './ModuleRow';
import { getInertProps } from './rowA11y';

type EvaluationPeriodSectionProps = {
  reorderMode: boolean;
  period: EvaluationPeriod;
  structureLevels: 1 | 2;
  isEdelvives: boolean;
  isExpanded: boolean;
  onToggleExpand: () => void;
  expandedModules: Record<string, boolean>;
  onToggleModuleExpansion: (moduleGuid: string) => void;
  onAddModule: () => void;
  onAddSubmodule: (parentModuleGuid: string) => void;
  activePeriodId: string | null;
  activeDragType: 'module' | 'submodule' | null;
  activeParentModuleGuid: string | null;
  onDelete?: (item: ModuleItemType | SubmoduleItem) => void;
  onEdit?: (item: ModuleItemType | SubmoduleItem) => void;
  onToggleVisibility?: (item: ModuleItemType | SubmoduleItem) => void;
  onOpenPhoto?: (item: ModuleItemType | SubmoduleItem) => void;
};

function EvaluationPeriodSection({
  reorderMode,
  period,
  structureLevels,
  isEdelvives,
  isExpanded,
  expandedModules,
  activePeriodId,
  activeDragType,
  activeParentModuleGuid,
  onToggleExpand,
  onToggleModuleExpansion,
  onAddModule,
  onAddSubmodule,
  onDelete,
  onEdit,
  onToggleVisibility,
  onOpenPhoto,
}: EvaluationPeriodSectionProps): JSX.Element {
  const { t } = useTranslate();

  const { setNodeRef, isOver } = useDroppable({
    id: period.id,
  });

  // Only show drag-over effect if dragging a module from a different period
  const shouldShowDragOver = isOver && activeDragType === 'module' && activePeriodId !== period.id;

  const moduleIds = useMemo(() => {
    return period.units
      .filter((unit) => !isEdelvives || unit.is_accessible === 1)
      .map((unit) => unit.guid);
  }, [period.units, isEdelvives]);

  const moduleCount = period.units.filter((unit) => !isEdelvives || unit.is_accessible === 1).length;
  const contentInertProps = getInertProps(!isExpanded);
  return (
    <div
      className={clsx('evaluation-period-section', {
        'evaluation-period-section--expanded': isExpanded,
        'evaluation-period-section--collapsed': !isExpanded,
        'evaluation-period-section--drag-over': shouldShowDragOver,
      })}
    >
      <button
        type="button"
        className={clsx('evaluation-period-section__header', {
          'evaluation-period-section__header--expanded': isExpanded,
        })}
        onClick={onToggleExpand}
        aria-expanded={isExpanded}
      >
        <div className="evaluation-period-section__header-left">
          <span className="evaluation-period-section__title">{stripHtml(t(`new-course:${period.name}`))}</span>
        </div>
        <div
          className={clsx('evaluation-period-section__chevron', {
            'evaluation-period-section__chevron--expanded': isExpanded,
          })}
        >
          <Icon name="chevron-down" size="medium" color="grey-03" />
        </div>
      </button>
      <div
        ref={setNodeRef}
        className={clsx('evaluation-period-section__content', {
          'evaluation-period-section__content--expanded': isExpanded,
          'evaluation-period-section__content--collapsed': !isExpanded,
        })}
        {...contentInertProps}
      >
        {moduleCount === 0 ? (
          <div className="evaluation-period-section__empty"></div>
        ) : (
          <SortableContext items={moduleIds} strategy={verticalListSortingStrategy}>
            {period.units
              .filter((unit) => !isEdelvives || unit.is_accessible === 1)
              .map((module) => (
                <ModuleRow
                  key={module.guid}
                  module={module}
                  structureLevels={structureLevels}
                  isExpanded={expandedModules[module.guid] ?? false}
                  onToggleExpand={() => onToggleModuleExpansion(module.guid)}
                  onAddSubmodule={onAddSubmodule}
                  activeDragType={activeDragType}
                  activeParentModuleGuid={activeParentModuleGuid}
                  reorderMode={reorderMode}
                  onDelete={onDelete}
                  onEdit={onEdit}
                  onToggleVisibility={onToggleVisibility}
                  onOpenPhoto={onOpenPhoto}
                />
              ))}
          </SortableContext>
        )}

        {/* Add Module Button */}
        {!reorderMode && <AddItemButton label={t('organize-content:Add module')} onClick={onAddModule} variant="module" />}
      </div>
    </div>
  );
}

export default EvaluationPeriodSection;
